import { useTranslation } from "react-i18next"
import type {
  Parameter,
  ParameterValue,
  BoolParameter,
  StringParameter,
  MultiSelectParameter,
  IntParameter,
  DateTimeParameter,
} from "@/types/parameter"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { useState } from "react"
import { cn } from "@/lib/utils"

const PARAMETER_FIELD_CLASS = "w-full space-y-2"
const PARAMETER_CONTROL_CLASS = "w-full"

interface ParameterRendererProps {
  parameter: Parameter
  onChange: (parameter: Parameter) => void
}

export function ParameterRenderer({
  parameter,
  onChange,
}: Readonly<ParameterRendererProps>) {
  switch (parameter.type) {
    case "Bool":
      return <BoolParameterRenderer parameter={parameter} onChange={onChange} />
    case "String":
      return (
        <StringParameterRenderer parameter={parameter} onChange={onChange} />
      )
    case "MultiSelect":
      return (
        <MultiSelectParameterRenderer
          parameter={parameter}
          onChange={onChange}
        />
      )
    case "Int":
      return <IntParameterRenderer parameter={parameter} onChange={onChange} />
    case "DateTime":
      return (
        <DateTimeParameterRenderer parameter={parameter} onChange={onChange} />
      )
    default:
      return null
  }
}

function BoolParameterRenderer({
  parameter,
  onChange,
}: Readonly<{
  parameter: BoolParameter
  onChange: (parameter: Parameter) => void
}>) {
  return (
    <div className="flex w-full items-center space-x-2">
      <Checkbox
        id={`${parameter.name}-checkbox`}
        checked={parameter.boolValue}
        disabled={!parameter.isEnabled}
        onCheckedChange={(checked) => {
          onChange({ ...parameter, boolValue: checked === true })
        }}
      />
      <Label
        htmlFor={`${parameter.name}-checkbox`}
        className="text-sm font-normal"
      >
        {parameter.text}
      </Label>
    </div>
  )
}

function StringParameterRenderer({
  parameter,
  onChange,
}: Readonly<{
  parameter: StringParameter
  onChange: (parameter: Parameter) => void
}>) {
  return (
    <AvailableValuesSelect
      name={parameter.name}
      text={parameter.text}
      value={parameter.stringValue}
      availableValues={parameter.availableValues}
      onValueChange={(value) => onChange({ ...parameter, stringValue: value })}
    />
  )
}

function AvailableValuesSelect({
  name,
  text,
  value,
  availableValues,
  onValueChange,
}: Readonly<{
  name: string
  text: string
  value: string
  availableValues: ParameterValue[]
  onValueChange: (value: string) => void
}>) {
  const { t } = useTranslation()
  // A value that is no longer offered shows as nothing selected, so it has to be re-picked.
  const selectedValue = availableValues.some(
    (option) => String(option.value) === value,
  )
    ? value
    : ""

  return (
    <div className={PARAMETER_FIELD_CLASS}>
      <Label htmlFor={name}>{text}</Label>
      <Select value={selectedValue} onValueChange={onValueChange}>
        <SelectTrigger id={name} className={PARAMETER_CONTROL_CLASS}>
          <SelectValue placeholder={t("parameterPanel.selectOption")} />
        </SelectTrigger>
        <SelectContent position="popper">
          {availableValues.map((option) => (
            <SelectItem
              key={String(option.value)}
              value={String(option.value)}
            >
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function MultiSelectParameterRenderer({
  parameter,
  onChange,
}: Readonly<{
  parameter: MultiSelectParameter
  onChange: (parameter: Parameter) => void
}>) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const maxVisibleTags = 3

  const selectedValues = parameter.stringValues || []
  const visibleTags = selectedValues.slice(0, maxVisibleTags)
  const hiddenCount = selectedValues.length - maxVisibleTags

  const toggleValue = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value]
    onChange({ ...parameter, stringValues: newValues })
  }

  const selectAll = () => {
    const newValues =
      selectedValues.length === parameter.availableValues.length
        ? []
        : parameter.availableValues.map((v) => String(v.value))
    onChange({ ...parameter, stringValues: newValues })
  }

  const removeValue = (value: string) => {
    onChange({
      ...parameter,
      stringValues: selectedValues.filter((v) => v !== value),
    })
  }

  return (
    <div className={PARAMETER_FIELD_CLASS}>
      <Label>{parameter.text}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={parameter.text}
            className="h-auto min-h-10 w-full justify-between"
          >
            <div className="flex min-w-0 flex-1 flex-wrap gap-1 overflow-hidden">
              {visibleTags.length === 0 && (
                <span className="truncate">
                  {t("parameterPanel.selectOptions")}
                </span>
              )}

              {visibleTags.map((value) => {
                const item = parameter.availableValues.find(
                  (v) => String(v.value) === value,
                )
                return (
                  <Badge
                    key={value}
                    variant="secondary"
                    className="max-w-full gap-1"
                  >
                    <span className="truncate">{item?.name || value}</span>
                    <X
                      className="h-3 w-3 shrink-0 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeValue(value)
                      }}
                    />
                  </Badge>
                )
              })}

              {hiddenCount > 0 && (
                <Badge variant="secondary" className="shrink-0">
                  +{hiddenCount} more
                </Badge>
              )}
            </div>

            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <div className="border-b p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={selectAll}
              >
                {selectedValues.length === parameter.availableValues.length
                  ? t("parameterPanel.deselectAll")
                  : t("parameters.selectAll")}
              </Button>
            </div>
            <CommandInput placeholder={t("parameterPanel.search")} />
            <CommandList>
              <CommandEmpty>{t("parameterPanel.noOptionsFound")}</CommandEmpty>
              <CommandGroup>
                {parameter.availableValues.map((val) => {
                  const value = String(val.value)
                  const isSelected = selectedValues.includes(value)
                  return (
                    <CommandItem
                      key={value}
                      value={value}
                      onSelect={() => toggleValue(value)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            "flex h-4 w-4 items-center justify-center rounded border",
                            isSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input",
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                        <span>{val.name}</span>
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

function IntParameterRenderer({
  parameter,
  onChange,
}: Readonly<{
  parameter: IntParameter
  onChange: (parameter: Parameter) => void
}>) {
  if (parameter.availableValues.length > 0) {
    return (
      <AvailableValuesSelect
        name={parameter.name}
        text={parameter.text}
        value={String(parameter.intValue)}
        availableValues={parameter.availableValues}
        onValueChange={(value) =>
          onChange({ ...parameter, intValue: Number.parseInt(value, 10) })
        }
      />
    )
  }

  return (
    <div className={PARAMETER_FIELD_CLASS}>
      <Label htmlFor={parameter.name}>{parameter.text}</Label>
      <Input
        id={parameter.name}
        type="number"
        value={parameter.intValue}
        className={PARAMETER_CONTROL_CLASS}
        onChange={(e) => {
          onChange({ ...parameter, intValue: Number.parseInt(e.target.value, 10) || 0 })
        }}
      />
    </div>
  )
}

function DateTimeParameterRenderer({
  parameter,
  onChange,
}: Readonly<{
  parameter: DateTimeParameter
  onChange: (parameter: Parameter) => void
}>) {
  return (
    <div className={PARAMETER_FIELD_CLASS}>
      <Label htmlFor={parameter.name}>{parameter.text}</Label>
      <Input
        id={parameter.name}
        type="datetime-local"
        value={parameter.dateTimeValue}
        className={PARAMETER_CONTROL_CLASS}
        onChange={(e) => {
          onChange({ ...parameter, dateTimeValue: e.target.value })
        }}
      />
    </div>
  )
}
